import Papa from "papaparse";
import { formatError } from "./bulkStageErrors";

// Builds a CSV containing only the rows that failed in the last upload, plus
// a final `_errors` column that joins all error messages for that row. The
// admin can fix the file offline and re-upload only the failures.
//
// `parsedRows` is the full array we sent (or attempted to send) — failures
// reference into it by `rowIndex`. If parsedRows is unavailable (e.g. trimmed
// from localStorage), we fall back to writing just {row, name, barcode, errors}
// so the user still gets *something* downloadable.
export function exportFailedRowsCsv({ rowFailures, parsedRows, fileName }) {
  if (!rowFailures || rowFailures.length === 0) return;

  const haveParsed = Array.isArray(parsedRows) && parsedRows.length > 0;
  let rows;

  if (haveParsed) {
    rows = rowFailures.map((failure) => {
      const original = parsedRows[failure.rowIndex] || {};
      return {
        ...original,
        _errors: failure.errors.map(formatError).join(" | "),
      };
    });
  } else {
    // Degraded: just the identity + errors. Useful as a list of what to fix
    // even without the original full row data.
    rows = rowFailures.map((failure) => ({
      row: failure.rowIndex + 1,
      name: failure.name || "",
      barcode: failure.barcode || "",
      _errors: failure.errors.map(formatError).join(" | "),
    }));
  }

  const csv = Papa.unparse(rows, { header: true });
  triggerDownload(csv, deriveDownloadName(fileName));
}

function deriveDownloadName(originalName, suffix = "failed") {
  const stamp = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 19);
  if (!originalName) return `bulk-upload-${suffix}-${stamp}.csv`;
  const base = originalName.replace(/\.(csv|xlsx|xls)$/i, "");
  return `${base}-${suffix}-${stamp}.csv`;
}

// Builds a CSV of every row that DIDN'T make it into the staging table when
// the stream ended incomplete. Combines two groups:
//
//   1. Rows the server explicitly rejected (from `rowFailures`) — same as
//      exportFailedRowsCsv would emit, marked _status="failed".
//   2. Rows after the last processed index (parsedRows[processed:]) — these
//      were sent but never returned an event, so they're presumed unstaged.
//      Marked _status="not_processed".
//
// Lets the admin save the file, fix the trigger row, and re-upload only what
// didn't land — without scrolling Pending Products to figure out where they
// were.
export function exportUnstagedRowsCsv({ rowFailures, parsedRows, processed, fileName }) {
  if (!Array.isArray(parsedRows) || parsedRows.length === 0) return;

  const failedIndices = new Set((rowFailures || []).map((f) => f.rowIndex));
  const unstaged = [];

  // 1) Explicit failures — emit with their original data + the joined errors.
  for (const failure of rowFailures || []) {
    const original = parsedRows[failure.rowIndex];
    if (!original) continue;
    unstaged.push({
      ...original,
      _status: "failed",
      _errors: failure.errors.map(formatError).join(" | "),
    });
  }

  // 2) Rows the server never responded to. `processed` counts row events
  //    received (ok + failed), so indices >= processed are unprocessed.
  //    Skip indices that already appear in failedIndices to avoid duplicates.
  for (let i = processed; i < parsedRows.length; i++) {
    if (failedIndices.has(i)) continue;
    unstaged.push({
      ...parsedRows[i],
      _status: "not_processed",
      _errors: "",
    });
  }

  if (unstaged.length === 0) return;
  const csv = Papa.unparse(unstaged, { header: true });
  triggerDownload(csv, deriveDownloadName(fileName, "unstaged"));
}

function triggerDownload(csv, name) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revoke so the browser has time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
